import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useApi } from '../../../design-system/useApi';
import { hrApi } from '../api';
import type { LeaveType, LeaveTypeRow, LeaveBalance, EmployeeWithUser } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmitted: () => void;
  /**
   * HR-on-behalf mode: when set, the request is created for this employee via
   * the HR endpoint instead of the self endpoint, and balances are shown.
   */
  forEmployeeId?: string;
  forEmployeeName?: string;
  /**
   * HR-on-behalf picker mode: when a list is passed (and `forEmployeeId` is not),
   * an employee picker is shown at the top and the request is filed for the
   * chosen employee via the HR endpoint.
   */
  employees?: EmployeeWithUser[];
}

const LEAVE_TYPES: LeaveType[] = ['vacation', 'sick', 'personal', 'unpaid', 'other'];

/** Map a configurable type code to the legacy enum value the column still requires. */
function codeToLegacyType(code: string): LeaveType {
  const c = code.toLowerCase();
  if (c === 'annual' || c === 'vacation') return 'vacation';
  if (c === 'sick') return 'sick';
  if (c === 'unpaid') return 'unpaid';
  if (c === 'personal') return 'personal';
  return 'other';
}

export function RequestLeaveDialog({ open, onOpenChange, onSubmitted, forEmployeeId, forEmployeeName, employees }: Props) {
  // Leave types are HR-gated; for plain employees this 403s and we fall back to
  // the legacy fixed type list.
  const typesQ = useApi<{ types: LeaveTypeRow[] }>(open ? '/api/hr/leave-types?active_only=1' : null, [open]);
  const types = typesQ.data?.types ?? [];
  const hasTypes = typesQ.error == null && types.length > 0;

  // Picker mode: HR files leave for an employee chosen from a dropdown.
  const pickerMode = !forEmployeeId && Array.isArray(employees);
  const [pickedEmployeeId, setPickedEmployeeId] = React.useState<string>('');
  // The employee this request is ultimately for (fixed or picked). When set,
  // we file via the HR-on-behalf endpoint and show balances.
  const onBehalfId = forEmployeeId ?? (pickerMode ? pickedEmployeeId : undefined);

  // Balances for the on-behalf employee (HR mode only).
  const balQ = useApi<{ balances: LeaveBalance[] }>(
    open && onBehalfId ? `/api/hr/employees/${onBehalfId}/leave-balances` : null,
    [open, onBehalfId],
  );

  const [legacyType, setLegacyType] = React.useState<LeaveType>('vacation');
  const [typeId, setTypeId] = React.useState<string>('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) { setLegacyType('vacation'); setTypeId(''); setFrom(''); setTo(''); setReason(''); setPickedEmployeeId(''); }
  }, [open]);

  const selectedBalance = onBehalfId && typeId
    ? (balQ.data?.balances ?? []).find((b) => b.leave_type_id === typeId)
    : undefined;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pickerMode && !pickedEmployeeId) { toast.error('Pick an employee'); return; }
    if (!from || !to) { toast.error('Pick a date range'); return; }

    let leave_type: LeaveType;
    let leave_type_id: string | null = null;
    if (hasTypes) {
      if (!typeId) { toast.error('Pick a leave type'); return; }
      const t = types.find((x) => x.id === typeId);
      leave_type = t ? codeToLegacyType(t.code) : 'other';
      leave_type_id = typeId;
    } else {
      leave_type = legacyType;
    }

    setBusy(true);
    try {
      if (onBehalfId) {
        await hrApi.submitLeaveForEmployee({ employee_id: onBehalfId, leave_type, leave_type_id, start_date: from, end_date: to, reason: reason || null });
      } else {
        await hrApi.submitLeave({ leave_type, leave_type_id, start_date: from, end_date: to, reason: reason || null });
      }
      toast.success('Request submitted');
      onSubmitted();
    } catch (err: any) {
      toast.error('Could not submit', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{pickerMode ? 'Log leave for a team member' : `Request leave${forEmployeeName ? ` — ${forEmployeeName}` : ''}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          {pickerMode && (
            <div className="grid gap-1.5">
              <Label>Employee</Label>
              <Select value={pickedEmployeeId} onValueChange={setPickedEmployeeId}>
                <SelectTrigger><SelectValue placeholder="Select an employee" /></SelectTrigger>
                <SelectContent>
                  {(employees ?? []).map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>Type</Label>
            {hasTypes ? (
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger><SelectValue placeholder="Select a leave type" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Select value={legacyType} onValueChange={(v) => setLegacyType(v as LeaveType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {selectedBalance && (
              <p className="text-[12px] text-muted-foreground mt-0.5">
                {selectedBalance.remaining} day{selectedBalance.remaining === 1 ? '' : 's'} remaining of {Number(selectedBalance.allocated) + Number(selectedBalance.carried_over)}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Submitting…' : 'Submit'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
